import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {AbstractTileComponent} from "./abstract-tile.component";
import type {KnowledgeObjectLinkTarget} from "../../../domain/entities/link-targets/knowledge-object-link-target";
import type {KnowledgeObjectType} from "../../../domain/entities/knowledge-object-type.type";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {KeyboardActivationHelper} from "../../../helpers/keyboard-activation.helper";

// Side-effect imports: registers <ha-dialog-whiteboard-view>/
// <ha-dialog-rte-view>. This component opens one of them directly (not
// through dashboard-creator), so it must not rely on some OTHER part of the
// app having loaded either first — the try/catch in `_open()` below only
// guards against a genuinely unregistered tag, which these imports make
// structurally unreachable.
import "../../_dialogs/ha-dialog-whiteboard-view";
import "../../_dialogs/ha-dialog-rte-view";

/** Which popup dialog tag opens for a given `KnowledgeObjectType` (Anke,
 *  2026-09-05: "die rte-dokumente... alles andere genauso wie beim richtigen
 *  whiteboard") — `'image'` has no readonly viewer yet, so it's absent from
 *  this map on purpose: absent here means `_open()` falls back to the "not
 *  supported" notice below, exactly like any future `KnowledgeObjectType`
 *  this map doesn't yet list. This map is where a future viewer would be
 *  added, not a data-model change — `KnowledgeObjectLinkTarget` is
 *  deliberately generic across all `KnowledgeObjectType`s already. */
const POPUP_TAG_BY_KNOWLEDGE_OBJECT_TYPE: Partial<Record<KnowledgeObjectType, string>> = {
    whiteboard: 'ha-dialog-whiteboard-view',
    document: 'ha-dialog-rte-view',
};

/**
 * A Linkview tile linking to a `KnowledgeObject` (Anke, 2026-09-04) — opens
 * the readonly popup matching its `knowledgeObjectType` on click
 * (`POPUP_TAG_BY_KNOWLEDGE_OBJECT_TYPE`), rather than navigating (this tile
 * has nothing to navigate TO in the sense a URL tile does). Deliberately a
 * `<div role="button" tabindex="0">`, NOT a real `<button>`: Gridstack's
 * `dd-draggable.js` refuses to start a drag whose `mousedown` target matches
 * its `skipMouseDown` selector, which includes `button` — a real button
 * filling the whole tile would make it undraggable while unlocked.
 */
@customElement('link-tile-knowledge-object')
export class LinkTileKnowledgeObjectComponent extends AbstractTileComponent<KnowledgeObjectLinkTarget> {

    protected override _renderBody(): HTMLTemplateResult {
        return html`
            <div class="dashboard-tile__link" role="button" tabindex="0"
                 @click=${this._open}
                 @keydown=${this._onKeydown}
            >
                ${this._renderIconAndText()}
            </div>
        `;
    }

    private readonly _onKeydown = KeyboardActivationHelper.onEnterOrSpace(() => this._open());

    /** try/catch guards against the resolved popup tag not being a
     *  registered custom element — structurally excluded by this file's own
     *  side-effect imports above, kept as defense in depth. */
    private _open(): void {
        const popupTag = POPUP_TAG_BY_KNOWLEDGE_OBJECT_TYPE[this.target.knowledgeObjectType];
        if (!popupTag) {
            UiHelper.notify({
                level: 'info',
                title: 'Nicht unterstützt',
                content: html`<p>Dieser Dokumenttyp wird noch nicht unterstützt.</p>`,
                closable: true,
            });
            return;
        }

        try {
            DialogHelper.showDialog(popupTag, {uid: this.target.uid});
        } catch (error) {
            // Review Lauf 03, MINOR: surfaced via UiHelper.notify (not just
            // console.error) for consistency with every other error path in
            // this feature — this branch is structurally unreachable today
            // (see the doc comment above), but silent failure would be a
            // trap if that ever changed.
            console.error(`Failed to open ${popupTag}`, error);
            UiHelper.notify({
                level: 'error',
                title: 'Fehler',
                content: html`<p>Das Dokument konnte nicht geöffnet werden.</p>`,
                closable: true,
            });
        }
    }

}
